import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import Heading from 'LuxKit/Typography/Heading'
import LayoutContainer from 'components/Common/LayoutContainer'
import Image from 'components/Common/Image'
import TextButton from 'LuxKit/Button/TextButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'

const SectionContainer = styled.section`
  width: 100%;
  padding: ${rem(40)} 0;
`

const BannerSurface = styled.div`
  width: 100%;
  padding: ${rem(40)} 0;
  background: ${props => props.theme.palette.neutral.surface.lightBackground};

  ${mediaQueryUp.tablet} {
    padding: ${rem(20)} 0;
  }
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  width: 100%;

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    align-items: center;
    gap: ${rem(32)};
  }
`

const ImageWrapper = styled.div`
  width: 100%;
  height: ${rem(200)};
  overflow: hidden;

  ${mediaQueryUp.tablet} {
    flex: 1 1 0;
    min-width: 0;
  }
`

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(20)};
  width: 100%;

  ${mediaQueryUp.tablet} {
    flex: 1 1 0;
    min-width: 0;
  }
`

function LuxPlusLandingFlightsBanner() {
  const intl = useIntl()
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const imageAlt = intl.formatMessage({
    id: 'oXp2XEouRsAKGA9RPzk0',
    defaultMessage: 'View from an aeroplane window above the clouds',
    description: 'Alt text for the LuxPlus flights banner image shown on the LuxPlus landing page',
  })

  return (
    <SectionContainer id="lux-plus-landing-flights-banner" data-testid="lux-plus-landing-flights-banner">
      <BannerSurface>
        <LayoutContainer>
          <Content>
            <ImageWrapper>
              <Image
                id="luxplus-landing-banner-9656ba981c47"
                alt={imageAlt}
                fit="center"
                loading="lazy"
              />
            </ImageWrapper>
            <Copy>
              <Heading variant="heading2">
                <FormattedMessage
                  id="08wHTNH4RSnhTmWmc-5K"
                  defaultMessage="Members <emphasis>save more</emphasis> on flights"
                  description="Heading for the flights pricing banner shown on the LuxPlus landing page"
                  values={{
                    emphasis: (chunks) => <em>{chunks}</em>,
                  }}
                />
              </Heading>
              <BodyTextBlock variant="large">
                <FormattedMessage
                  id="8KGsWg6Fi08pKP45_QxR"
                  defaultMessage="LuxPlus+ members save on flights to hundreds of destinations worldwide"
                  description="Body copy explaining the flights pricing benefit on the LuxPlus landing page"
                />
              </BodyTextBlock>
              <ProductPaletteProvider palette="lux-plus">
                <TextButton
                  kind="primary"
                  size="medium"
                  shape="square"
                  fit="mobile-full-width"
                  to={canRedeemLuxPlusBenefits ? '/flights' : '/checkout/lux-plus'}
                >
                  {canRedeemLuxPlusBenefits ? (
                    <FormattedMessage
                      id="d_iVetFobHeo-zfbtN1t"
                      defaultMessage="Search flights"
                      description="Call to action button text for the LuxPlus landing flights banner shown to members"
                    />
                  ) : (
                    <FormattedMessage
                      id="7iBr2HoxP0d0aA4m4i-g"
                      defaultMessage="Become a member"
                      description="Call to action button text for the LuxPlus landing flights banner shown to non-members"
                    />
                  )}
                </TextButton>
              </ProductPaletteProvider>
            </Copy>
          </Content>
        </LayoutContainer>
      </BannerSurface>
    </SectionContainer>
  )
}

export default LuxPlusLandingFlightsBanner
